import React from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { LangProvider } from "@/lib/i18n";
import { MetaProvider } from "@/lib/meta";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import { registerServiceWorker } from "@/lib/push";

// Register service worker (push) once at startup
if (typeof window !== "undefined") {
  registerServiceWorker();
}

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CreateRequest from "@/pages/CreateRequest";
import RequestDetails from "@/pages/RequestDetails";
import MyRequests from "@/pages/MyRequests";
import MerchantDashboard from "@/pages/MerchantDashboard";
import MerchantFeed from "@/pages/MerchantFeed";
import MerchantRequestDetail from "@/pages/MerchantRequestDetail";
import MerchantOffers from "@/pages/MerchantOffers";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminSecurity from "@/pages/AdminSecurity";
import AdminActivity from "@/pages/AdminActivity";
import Profile from "@/pages/Profile";
import Notifications from "@/pages/Notifications";
import AuthCallback from "@/pages/AuthCallback";
import MerchantAnalytics from "@/pages/MerchantAnalytics";
import MerchantPublicProfile from "@/pages/MerchantPublicProfile";
import MerchantBoost from "@/pages/MerchantBoost";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";

function RoleHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  if (user.role === "customer") return <Navigate to="/customer" replace />;
  if (user.role === "merchant") return <Navigate to="/merchant" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  return <Navigate to="/" replace />;
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <MetaProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/m/:id" element={<MerchantPublicProfile />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/home" element={<RoleHome />} />

            <Route path="/customer" element={<ProtectedRoute roles={["customer"]}><CustomerDashboard /></ProtectedRoute>} />
            <Route path="/customer/new" element={<ProtectedRoute roles={["customer"]}><CreateRequest /></ProtectedRoute>} />
            <Route path="/customer/requests" element={<ProtectedRoute roles={["customer"]}><MyRequests /></ProtectedRoute>} />
            <Route path="/customer/request/:id" element={<ProtectedRoute roles={["customer"]}><RequestDetails /></ProtectedRoute>} />

            <Route path="/merchant" element={<ProtectedRoute roles={["merchant"]}><MerchantDashboard /></ProtectedRoute>} />
            <Route path="/merchant/feed" element={<ProtectedRoute roles={["merchant"]}><MerchantFeed /></ProtectedRoute>} />
            <Route path="/merchant/offers" element={<ProtectedRoute roles={["merchant"]}><MerchantOffers /></ProtectedRoute>} />
            <Route path="/merchant/analytics" element={<ProtectedRoute roles={["merchant"]}><MerchantAnalytics /></ProtectedRoute>} />
            <Route path="/merchant/boost" element={<ProtectedRoute roles={["merchant"]}><MerchantBoost /></ProtectedRoute>} />
            <Route path="/merchant/request/:id" element={<ProtectedRoute roles={["merchant"]}><MerchantRequestDetail /></ProtectedRoute>} />

            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/merchants" element={<Navigate to="/admin?tab=pending" replace />} />
            <Route path="/admin/orders" element={<Navigate to="/admin?tab=accepted" replace />} />
            <Route path="/admin/security" element={<ProtectedRoute roles={["admin"]}><AdminSecurity /></ProtectedRoute>} />
            <Route path="/admin/activity" element={<ProtectedRoute roles={["admin"]}><AdminActivity /></ProtectedRoute>} />

            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
        </MetaProvider>
      </AuthProvider>
    </LangProvider>
  );
}
